import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Card, Table, Button, Modal, Form, Input, Select, Tabs, message, Popconfirm, Space, Upload, Progress, Tooltip, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, PictureOutlined, UploadOutlined, ArrowLeftOutlined, PlayCircleOutlined, ImportOutlined, ExportOutlined, FileExcelOutlined, FileTextOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { assetApi, aiApi, projectApi, api } from '../services/api';



// 修改 buildImageUrl 函数，确保返回有效的 URL
const buildImageUrl = (imgPath, thumbnail = false) => {
  if (!imgPath) return null;
  
  // 处理数组类型的路径
  if (Array.isArray(imgPath) && imgPath.length > 0) {
    imgPath = imgPath[0]; // 使用第一个路径
  }
  
  // 处理JSON数组格式的路径字符串
  if (typeof imgPath === 'string' && (imgPath.startsWith('[') || imgPath.includes('['))) {
    try {
      const parsed = JSON.parse(imgPath);
      if (Array.isArray(parsed) && parsed.length > 0) {
        imgPath = parsed[0]; // 使用第一个路径
      }
    } catch (e) {
      // 如果解析失败，继续处理
    }
  }
  
  // 如果不是字符串，直接返回
  if (typeof imgPath !== 'string') {
    return null;
  }
  
  // 如果已经是完整URL，直接返回
  if (imgPath.startsWith('http')) {
    return imgPath;
  }
  
  // 兼容旧数据：去掉 manju/ 前缀（如果存在）
  let path = imgPath;
  if (path.startsWith('manju/')) {
    path = path.substring(6); // 去掉 'manju/' 前缀
  }
  
  // 如果路径以 /shortdrama 开头，直接使用
  // 如果路径以 data/ 开头，添加 /shortdrama 前缀
  // 其他情况，确保路径以 / 开头
  if (path.startsWith('/shortdrama/')) {
    // 路径正确，不需要修改
  } else if (path.startsWith('shortdrama/')) {
    path = '/' + path;
  } else if (path.startsWith('/data/') || path.startsWith('data/')) {
    // data/projects/* 需要添加 /shortdrama 前缀
    path = path.startsWith('/') ? '/shortdrama' + path : '/shortdrama/' + path;
  } else if (!path.startsWith('/')) {
    path = '/' + path;
  }

  // 检查是否是双重编码（包含 %25 表示 % 被编码了）
  if (path.includes('%25')) {
    // 双重编码，需要解码一次
    try {
      path = decodeURIComponent(path);
    } catch (e) {
      console.warn('解码失败:', path);
    }
  }

  // 检查路径是否已经被 URL 编码
  const hasEncodedChars = /%[0-9A-Fa-f]{2}/.test(path);
  if (hasEncodedChars) {
    // 路径已经被编码，直接返回
    const timestamp = Date.now();
    return path + '?t=' + timestamp + (thumbnail ? '&thumbnail=1' : '');
  }

  // 对路径进行 URL 编码，确保中文文件名正确处理
  // 只编码文件名部分，保留 / 作为路径分隔符
  const parts = path.split('/');
  const encodedParts = parts.map(part => encodeURIComponent(part));
  const encodedPath = encodedParts.join('/');
  // 添加时间戳参数，防止浏览器缓存
  const timestamp = Date.now();
  return encodedPath + '?t=' + timestamp + (thumbnail ? '&thumbnail=1' : '');
};



 
const { Option } = Select;
const { TabPane } = Tabs;
const { TextArea } = Input;

const AssetsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState('角色');
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewImages, setPreviewImages] = useState([]);
  
  // 批量操作相关状态
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isBatchGenerateModalVisible, setIsBatchGenerateModalVisible] = useState(false);
  const [batchGenerateForm] = Form.useForm();
  const [isBatchGenerating, setIsBatchGenerating] = useState(false);

  const [script, setScript] = useState('');

  // 剧本分析相关状态
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isProgressModalVisible, setIsProgressModalVisible] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState({ current: 0, total: 0, episode_title: '' });
  
  // 导入导出相关状态
  const [isImportModalVisible, setIsImportModalVisible] = useState(false);
  const [isExportModalVisible, setIsExportModalVisible] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importFormat, setImportFormat] = useState('json'); // 'json' 或 'excel'
  
  // 图片预览相关状态
  const [previewImageModalVisible, setPreviewImageModalVisible] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState('');
  const [imageScale, setImageScale] = useState(1);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  
  // 监控模态框状态变化
  useEffect(() => {
    console.log('=== 模态框状态变化 ===');
    console.log('当前模态框显示状态:', previewImageModalVisible);
    console.log('当前预览图片URL:', previewImageUrl);
    console.log('=== 模态框状态变化结束 ===');
  }, [previewImageModalVisible, previewImageUrl]);

  const loadAssets = useCallback(async () => {
    try {
      const response = await assetApi.getAll(id);
      const loadedAssets = response.data;
      setAssets(loadedAssets);
      
      // 检查是否有未完成的图片生成任务
      const pendingAssets = loadedAssets.filter(a => a.image_generating && a.image_task_id);
      if (pendingAssets.length > 0) {
        message.info(`发现 ${pendingAssets.length} 个未完成的图片生成任务，正在恢复...`);
        // 恢复所有未完成的任务
        pendingAssets.forEach(asset => {
          recoverImageTask(asset);
        });
      }
    } catch (error) {
      message.error('加载资产失败');
    }
  }, [id]);
  
  // 恢复中断的图片生成任务
  const recoverImageTask = async (asset) => {
    if (!asset.image_task_id) return;
    
    try {
      const taskData = asset.image_generate_params || {};
      taskData.project_id = id;
      taskData.asset_id = asset.id;
      
      // 轮询任务状态
      const maxRetries = 60;
      const interval = 3000;
      
      for (let i = 0; i < maxRetries; i++) {
        try {
          const result = await aiApi.getImageGenerationStatus(asset.image_task_id, taskData);
          
          if (result.data.success) {
            if (result.data.status === 'completed') {
              message.success(`资产 "${asset.name}" 图片生成已完成`);
              // 更新本地状态
              setAssets(prev => prev.map(a => 
                a.id === asset.id 
                  ? { ...a, image_path: result.data.local_path || a.image_path, image_generating: false, image_task_id: '' }
                  : a
              ));
              loadAssets(); // 重新加载以获取最新状态
              return;
            } else if (result.data.status === 'failed') {
              message.error(`资产 "${asset.name}" 图片生成失败: ${result.data.error || '未知错误'}`);
              // 标记为失败
              setAssets(prev => prev.map(a => 
                a.id === asset.id 
                  ? { ...a, image_generating: false, image_task_id: '' }
                  : a
              ));
              return;
            }
          }
        } catch (error) {
          console.error(`恢复任务 ${asset.image_task_id} 失败:`, error);
        }
        
        await new Promise(resolve => setTimeout(resolve, interval));
      }
      
      message.warning(`资产 "${asset.name}" 图片生成任务超时`);
    } catch (error) {
      console.error('恢复图片任务失败:', error);
    }
  };

  // 加载剧本内容（用于识别资产）
  const loadScript = useCallback(async () => {
    try {
      const scriptRes = await projectApi.getById(id);
      setScript(scriptRes.data.script_content || '');
    } catch (error) {
      console.error('加载剧本失败:', error);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      loadAssets();
      loadScript();
    }
  }, [id, loadAssets, loadScript]);

  // 组件卸载时清理轮询
  useEffect(() => {
    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
        console.log('[AssetsPage] 组件卸载，清理轮询');
      }
    };
  }, []);

  // 监听资产更新事件
  useEffect(() => {
    const handleAssetsUpdated = (event) => {
      if (event.detail?.projectId === id) {
        console.log('收到资产更新事件，刷新资产列表');
        loadAssets();
        // 显示更新信息
        const { characters, props, scenes } = event.detail;
        const totalCount = (characters?.length || 0) + (props?.length || 0) + (scenes?.length || 0);
        if (totalCount > 0) {
          message.success(`资产已更新！新增 ${totalCount} 个资产`);
        }
      }
    };

    window.addEventListener('assets-updated', handleAssetsUpdated);
    return () => {
      window.removeEventListener('assets-updated', handleAssetsUpdated);
    };
  }, [id, loadAssets]);

  const handleCreateAsset = async (values) => {
    try {
      // 获取图片路径的优先级：
      // 1. values.image_path（表单中的值，数组）
      // 2. previewImages（从完整URL提取路径，数组）
      // 3. editingAsset?.image_path（编辑时的原始值）
      let imagePaths = values.image_path || [];

      console.log('DEBUG - values.image_path:', values.image_path);
      console.log('DEBUG - previewImages:', previewImages);
      console.log('DEBUG - editingAsset?.image_path:', editingAsset?.image_path);

      // 如果没有表单值，但有 previewImages，从 previewImages 提取
      if (!imagePaths || imagePaths.length === 0) {
        imagePaths = previewImages.map(imageUrl => {
          try {
            let path = imageUrl;
            
            // 如果是完整 URL，提取 pathname
            if (imageUrl.startsWith('http')) {
              const url = new URL(imageUrl);
              path = url.pathname;
            }
            
            // 去掉前导 /
            if (path.startsWith('/')) {
              path = path.substring(1);
            }
            // 去掉时间戳参数（如果有）
            if (path.includes('?')) {
              path = path.split('?')[0];
            }
            return path;
          } catch (e) {
            console.warn('从 previewImage 提取路径失败:', imageUrl, e);
            return '';
          }
        }).filter(Boolean);
      }

      // 如果还是没有，使用编辑时的原始值
      if (!imagePaths || imagePaths.length === 0) {
        if (editingAsset?.image_path) {
          let originalPath = editingAsset.image_path;
          // 如果是 JSON 字符串，先解析
          if (typeof originalPath === 'string' && originalPath.startsWith('[')) {
            try {
              originalPath = JSON.parse(originalPath);
            } catch (e) {
              console.warn('解析 image_path JSON 失败:', e);
            }
          }
          imagePaths = Array.isArray(originalPath) ? originalPath : [originalPath];
        } else {
          imagePaths = [];
        }
      }

      // 处理 imagePaths 中的完整URL
      imagePaths = imagePaths.map(path => {
        if (path && path.startsWith('http')) {
          try {
            const url = new URL(path);
            let newPath = url.pathname;
            // 去掉前导 /
            if (newPath.startsWith('/')) {
              newPath = newPath.substring(1);
            }
            return newPath;
          } catch (e) {
            console.warn('URL解析失败:', path);
            return path;
          }
        }
        return path;
      });

      // 构建资产数据
      const assetData = { ...values };

      console.log('DEBUG - 最终 imagePaths:', imagePaths);
      console.log('DEBUG - editingAsset?.image_path:', editingAsset?.image_path);

      // 优先使用新的 imagePaths
      if (imagePaths && imagePaths.length > 0) {
        // 将图片路径数组转换为JSON字符串存储
        assetData.image_path = JSON.stringify(imagePaths);
        console.log('DEBUG - 使用新的 imagePaths (JSON字符串):', assetData.image_path);
      } else {
        // 清空图片路径
        assetData.image_path = '';
        console.log('DEBUG - 清空 image_path');
      }

      console.log('DEBUG - assetData.image_path:', assetData.image_path);

      if (editingAsset) {
        await assetApi.update(id, editingAsset.id, assetData);
        message.success('资产已更新');
      } else {
        await assetApi.create(id, assetData);
        message.success('资产已创建');
      }
      setIsModalVisible(false);
      form.resetFields();
      setEditingAsset(null);
      setPreviewImages([]);
      loadAssets();
    } catch (error) {
      message.error('操作失败');
    }
  };

  const handleDeleteAsset = async (assetId) => {
    try {
      await assetApi.delete(id, assetId);
      message.success('资产已删除');
      loadAssets();
    } catch (error) {
      message.error('删除失败');
    }
  };

  // 批量删除
  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要删除的资产');
      return;
    }
    
    Modal.confirm({
      title: '确认批量删除',
      content: `确定要删除选中的 ${selectedRowKeys.length} 个资产吗？此操作不可恢复。`,
      okText: '删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          // 逐个删除选中的资产
          for (const assetId of selectedRowKeys) {
            await assetApi.delete(id, assetId);
          }
          message.success(`成功删除 ${selectedRowKeys.length} 个资产`);
          setSelectedRowKeys([]);
          loadAssets();
        } catch (error) {
          message.error('批量删除失败');
        }
      }
    });
  };

  // 打开批量出图对话框
  const openBatchGenerateModal = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要出图的资产');
      return;
    }
    batchGenerateForm.resetFields();
    batchGenerateForm.setFieldsValue({
      aspect_ratio: '1:1',
      orientation: 'square'
    });
    setIsBatchGenerateModalVisible(true);
  };

  // 批量出图
  const handleBatchGenerate = async (values) => {
    const { aspect_ratio, orientation } = values;
    
    // 获取选中的资产
    const selectedAssets = assets.filter(asset => selectedRowKeys.includes(asset.id));
    
    setIsBatchGenerating(true);
    let successCount = 0;
    let failCount = 0;
    
    for (const asset of selectedAssets) {
      const prompt = asset.prompt;
      const description = asset.description;
      
      if (!prompt) {
        message.warning(`资产 "${asset.name}" 没有生图提示词，跳过`);
        failCount++;
        continue;
      }
      
      // 合并生图提示词和主体描述
      let combinedPrompt = prompt;
      if (description) {
        combinedPrompt = `${prompt}\n\n参考描述：${description}`;
      }
      
      try {
        const result = await aiApi.generateImage(combinedPrompt, { 
          asset_type: asset.type,
          project_id: id,
          asset_id: asset.id,
          aspect_ratio: aspect_ratio,
          orientation: orientation
        });
        
        if (result.data.success) {
          if (result.data.is_async && result.data.task_id) {
            await pollBatchImageStatus(result.data.task_id, result.data, asset.name);
            successCount++;
          } else {
            successCount++;
          }
        } else {
          failCount++;
          message.error(`资产 "${asset.name}" 生成失败: ${result.data.error || '未知错误'}`);
        }
      } catch (error) {
        failCount++;
        message.error(`资产 "${asset.name}" 生成失败: ${error.message}`);
      }
    }
    
    setIsBatchGenerating(false);
    setIsBatchGenerateModalVisible(false);
    
    if (successCount > 0) {
      message.success(`成功生成 ${successCount} 张图片`);
      loadAssets();
    }
    if (failCount > 0) {
      message.error(`${failCount} 个资产生成失败`);
    }
    
    // 清空选择
    setSelectedRowKeys([]);
  };

  const handleGenerateImage = async () => {
    const prompt = form.getFieldValue('prompt');
    const description = form.getFieldValue('description');
    
    if (!prompt) {
      message.error('请先生成或填写生图提示词');
      return;
    }
    
    // 合并生图提示词和主体描述
    let combinedPrompt = prompt;
    if (description) {
      combinedPrompt = `${prompt}\n\n参考描述：${description}`;
    }
    
    setIsGenerating(true);
    try {
      const result = await aiApi.generateImage(combinedPrompt, { 
        asset_type: form.getFieldValue('type'),
        project_id: id,
        asset_id: editingAsset?.id
      });
      
      if (result.data.success) {
        if (result.data.is_async && result.data.task_id) {
          message.info('图片生成任务已提交，请等待...');
          await pollImageStatus(result.data.task_id, result.data);
        } else {
          message.success(result.data.warning || '图片生成成功');
          const imagePath = result.data.local_path || result.data.image_url;
          if (imagePath) {
            const fullUrl = buildImageUrl(imagePath);
            setPreviewImages(prev => {
              const newImages = [...prev, fullUrl];
              if (newImages.length > 5) {
                newImages.splice(0, newImages.length - 5);
              }
              const imagePaths = newImages.map(imgUrl => {
                try {
                  if (imgUrl.startsWith('http')) {
                    const url = new URL(imgUrl);
                    let path = url.pathname;
                    if (path.startsWith('/')) {
                      path = path.substring(1);
                    }
                    if (path.includes('?')) {
                      path = path.split('?')[0];
                    }
                    return path;
                  }
                  return imgUrl;
                } catch (e) {
                  return imgUrl;
                }
              });
              form.setFieldsValue({ image_path: imagePaths });
              return newImages;
            });
          }
        }
      } else {
        message.error(result.data.error || '图片生成失败');
      }
    } catch (error) {
      message.error('图片生成失败: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsGenerating(false);
    }
  };

  const pollImageStatus = async (taskId, taskData) => {
    const maxRetries = 120;  // 最多等待 10 分钟
    const interval = 5000;  // 每 5 秒检查一次
    
    for (let i = 0; i < maxRetries; i++) {
      try {
        const result = await aiApi.getImageGenerationStatus(taskId, taskData);
        const data = result.data;
        
        if (data.success) {
          if (data.status === 'completed') {
            message.success('图片生成成功');
            const imagePath = data.local_path || data.image_url;
            if (imagePath) {
              const fullUrl = buildImageUrl(imagePath);
              setPreviewImages(prev => {
                const newImages = [...prev, fullUrl];
                if (newImages.length > 5) {
                  newImages.splice(0, newImages.length - 5);
                }
                const imagePaths = newImages.map(imgUrl => {
                  try {
                    if (imgUrl.startsWith('http')) {
                      const url = new URL(imgUrl);
                      let path = url.pathname;
                      if (path.startsWith('/')) {
                        path = path.substring(1);
                      }
                      if (path.includes('?')) {
                        path = path.split('?')[0];
                      }
                      return path;
                    }
                    return imgUrl;
                  } catch (e) {
                    return imgUrl;
                  }
                });
                form.setFieldsValue({ image_path: imagePaths });
                return newImages;
              });
            }
            return;
          } else if (data.status === 'failed') {
            message.error(data.error || '图片生成失败');
            return;
          }
        } else {
          // 检查是否是任务明确失败（而不是网络错误）
          if (data.status === 'failed') {
            // 任务真正失败了，直接显示错误，停止重试
            const errorMsg = data.error || '图片生成失败';
            console.error('图片生成任务失败:', errorMsg);
            message.error(errorMsg);
            return;
          }
          // 网络错误可能是暂时的，继续重试
          if (i > 0) {
            console.warn(`查询任务状态失败（第${i+1}次）:`, data.error);
          } else {
            // 第一次就失败，如果是任务失败（非网络错误）直接显示
            const isNetworkError = !data.status && (!data.error || 
              String(data.error).includes('HTTP') || 
              String(data.error).includes('Network') ||
              String(data.error).includes('超时'));
            if (!isNetworkError) {
              message.error(data.error || '查询任务状态失败');
              return;
            }
            console.warn('首次查询状态失败，继续重试:', data.error);
          }
        }
      } catch (error) {
        // 网络错误可能是暂时的，继续重试
        if (i > 0) {
          console.warn(`查询图片生成状态失败（第${i+1}次）:`, error.message);
        } else {
          console.error('查询图片生成状态失败:', error);
        }
      }
      
      await new Promise(resolve => setTimeout(resolve, interval));
    }
    
    message.error('图片生成超时（已等待10分钟）');
  };

  const pollBatchImageStatus = async (taskId, taskData, assetName) => {
    const maxRetries = 120;  // 最多等待 10 分钟
    const interval = 5000;  // 每 5 秒检查一次
    
    for (let i = 0; i < maxRetries; i++) {
      try {
        const result = await aiApi.getImageGenerationStatus(taskId, taskData);
        const data = result.data;
        
        if (data.success) {
          if (data.status === 'completed') {
            return;
          } else if (data.status === 'failed') {
            message.error(`资产 "${assetName}" 生成失败: ${data.error || '未知错误'}`);
            return;
          }
        } else {
          // 检查是否是任务明确失败（而不是网络错误）
          if (data.status === 'failed') {
            // 任务真正失败了，直接显示错误，停止重试
            message.error(`资产 "${assetName}" 生成失败: ${data.error || '未知错误'}`);
            return;
          }
          // 网络错误可能是暂时的，继续重试
          if (i > 0) {
            console.warn(`资产 "${assetName}" 查询状态失败（第${i+1}次）:`, data.error);
          } else {
            // 第一次就失败，如果是任务失败（非网络错误）直接显示
            const isNetworkError = !data.status && (!data.error || 
              String(data.error).includes('HTTP') || 
              String(data.error).includes('Network') ||
              String(data.error).includes('超时'));
            if (!isNetworkError) {
              message.error(`资产 "${assetName}" 查询状态失败: ${data.error || '未知错误'}`);
              return;
            }
            console.warn(`资产 "${assetName}" 首次查询状态失败，继续重试:`, data.error);
          }
        }
      } catch (error) {
        // 网络错误可能是暂时的，继续重试
        if (i > 0) {
          console.warn(`资产 "${assetName}" 查询图片生成状态失败（第${i+1}次）:`, error.message);
        } else {
          console.error(`资产 "${assetName}" 查询图片生成状态失败:`, error);
        }
      }
      
      await new Promise(resolve => setTimeout(resolve, interval));
    }
    
    message.error(`资产 "${assetName}" 生成超时（已等待10分钟）`);
  };

  const openEditModal = (asset) => {
    setEditingAsset(asset);
    form.setFieldsValue(asset);
    // 构建完整的图片URL用于预览
    const imgPath = asset.image_path;
    if (imgPath) {
      let imagePaths = [];
      if (Array.isArray(imgPath)) {
        imagePaths = imgPath;
      } else if (typeof imgPath === 'string') {
        // 尝试解析JSON字符串
        try {
          const parsed = JSON.parse(imgPath);
          if (Array.isArray(parsed)) {
            imagePaths = parsed;
          } else {
            imagePaths = [imgPath];
          }
        } catch (e) {
          // 如果不是JSON，作为单个路径处理
          imagePaths = [imgPath];
        }
      } else {
        imagePaths = [imgPath];
      }
      const fullUrls = imagePaths.map(path => buildImageUrl(path));
      setPreviewImages(fullUrls);
    } else {
      setPreviewImages([]);
    }
    setIsModalVisible(true);
  };

  const openCreateModal = () => {
    setEditingAsset(null);
    form.resetFields();
    setPreviewImages([]);
    form.setFieldsValue({ type: activeTab });
    setIsModalVisible(true);
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setEditingAsset(null);
    setPreviewImages([]);
    form.resetFields();
  };

  // 表格行选择配置
  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys) => {
      setSelectedRowKeys(newSelectedRowKeys);
    }
  };

  const columns = [
    {
      title: '序号',
      key: 'index',
      width: 60,
      render: (_, __, index) => index + 1
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 80,
      render: (type) => {
        const colorMap = {
          '角色': 'blue',
          '道具': 'green',
          '场景': 'orange'
        };
        return <span style={{ color: colorMap[type] || '#333', fontWeight: 'bold' }}>{type}</span>;
      }
    },
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
      width: 120
    },
    {
      title: '生图提示词',
      dataIndex: 'prompt',
      key: 'prompt',
      ellipsis: true,
      render: (prompt) => (
        <div style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {prompt || '-'}
        </div>
      )
    },
    {
      title: '主体描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (desc) => (
        <div style={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {desc || '-'}
        </div>
      )
    },
    {
      title: '出现集数',
      dataIndex: 'episodes',
      key: 'episodes',
      width: 100,
      render: (episodes) => {
        if (!episodes) return '-';
        try {
          // 解析 JSON 字符串
          const episodeList = JSON.parse(episodes);
          if (Array.isArray(episodeList) && episodeList.length > 0) {
            return episodeList.join(', ');
          }
          return '-';
        } catch (e) {
          // 如果不是 JSON，直接显示
          return episodes || '-';
        }
      }
    },
    {
      title: '参考图',
      dataIndex: 'image_path',
      key: 'image_path',
      width: 300,
      render: (img, record) => {
        // 检查是否正在生成图片
        if (record.image_generating) {
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ 
                width: 60, 
                height: 60, 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px dashed #1890ff',
                borderRadius: 4,
                backgroundColor: '#e6f7ff',
                fontSize: 12,
                color: '#1890ff'
              }}>
                生成中
              </div>
              {record.image_task_id && (
                <Button 
                  type="link" 
                  size="small"
                  onClick={() => recoverImageTask(record)}
                >
                  恢复
                </Button>
              )}
            </div>
          );
        }
        
        let imagePaths = [];
        if (img) {
          if (Array.isArray(img)) {
            imagePaths = img;
          } else if (typeof img === 'string') {
            // 尝试解析JSON字符串
            try {
              const parsed = JSON.parse(img);
              if (Array.isArray(parsed)) {
                imagePaths = parsed;
              } else {
                imagePaths = [img];
              }
            } catch (e) {
              // 如果不是JSON，作为单个路径处理
              imagePaths = [img];
            }
          } else {
            imagePaths = [img];
          }
        }
        
        // 清理和验证图片路径
        const validImagePaths = imagePaths.filter(path => {
          if (!path || typeof path !== 'string') return false;
          // 移除引号（如果有）
          let cleanPath = path.replace(/^"|"$/g, '').replace(/^'|'$/g, '');
          // 移除首尾空格
          cleanPath = cleanPath.trim();
          // 确保清理后路径不为空
          return cleanPath.length > 0;
        }).map(path => {
          // 移除引号（如果有）
          let cleanPath = path.replace(/^"|"$/g, '').replace(/^'|'$/g, '');
          // 移除首尾空格
          cleanPath = cleanPath.trim();
          return cleanPath;
        });
        
        // 处理图片预览
        const handleShowAllImages = () => {
          // 构建所有图片的完整URL
          const allImageUrls = validImagePaths.map(path => buildImageUrl(path));
          // 弹出模态框显示所有图片
          if (allImageUrls.length > 0) {
            setPreviewImageUrl(allImageUrls[0]);
            setPreviewImageModalVisible(true);
          }
        };
        
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {validImagePaths.map((path, index) => {
                const thumbnailUrl = buildImageUrl(path, true); // 使用缩略图
                const fullImageUrl = buildImageUrl(path); // 原图用于点击放大
                return (
                  <div 
                    key={index} // 使用索引作为key，确保每次渲染都能正确更新
                    style={{ 
                      width: 60, 
                      height: 60, 
                      border: '1px solid #d9d9d9',
                      borderRadius: 4,
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      backgroundColor: '#f5f5f5'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setPreviewImageUrl(fullImageUrl);
                      setPreviewImageModalVisible(true);
                    }}
                  >
                    <img 
                      src={thumbnailUrl} 
                      alt={`参考图 ${index + 1}`} 
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '100%', 
                        objectFit: 'cover'
                      }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '';
                      }}
                    />
                  </div>
                );
              })}
              {validImagePaths.length === 0 && (
                <div style={{ 
                  width: 60, 
                  height: 60, 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid #d9d9d9',
                  borderRadius: 4,
                  backgroundColor: '#f5f5f5',
                  fontSize: 12,
                  color: '#999'
                }}>
                  无图
                </div>
              )}
            </div>
          </div>
        );
      }
    },
    {
      title: '参考声音',
      dataIndex: 'voice_path',
      key: 'voice_path',
      width: 80,
      render: (voice) => voice ? <span style={{ color: '#52c41a' }}>有</span> : <span style={{ color: '#999' }}>无</span>
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => openEditModal(record)}
          >
            编辑
          </Button>
          <Popconfirm title="确定删除这个资产吗？" onConfirm={() => handleDeleteAsset(record.id)}>
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const filteredAssets = assets.filter(asset => asset.type === activeTab);

  // 剧本分析功能
  const intervalIdRef = useRef(null);

  const pollAnalysisTaskStatus = useCallback(async (taskId) => {
    try {
      const response = await api.get(`/ai/analyze-script/status/${taskId}`);
      const { status, progress, result, error } = response.data;
      
      setAnalysisProgress(progress);
      
      if (status === 'completed' || status === 'failed') {
        // 清除轮询
        if (intervalIdRef.current) {
          clearInterval(intervalIdRef.current);
          intervalIdRef.current = null;
        }
        
        setIsProgressModalVisible(false);
        setIsAnalyzing(false);
        
        if (status === 'completed') {
          // 检查是否是追加资产任务
          if (result.new_characters || result.new_props || result.new_scenes) {
            const newCharCount = result.new_characters?.length || 0;
            const newPropCount = result.new_props?.length || 0;
            const newSceneCount = result.new_scenes?.length || 0;
            
            message.success(`资产追加完成！新增 ${newCharCount} 个角色、${newPropCount} 个道具、${newSceneCount} 个场景`);
          } else {
            const charCount = result.characters?.length || 0;
            const propCount = result.props?.length || 0;
            const sceneCount = result.scenes?.length || 0;
            
            message.success(`分析完成！识别到 ${charCount} 个角色、${propCount} 个道具、${sceneCount} 个场景`);
          }
          loadAssets();
        } else {
          message.error('剧本分析失败: ' + (error || '未知错误'));
        }
      }
    } catch (error) {
      console.error('获取任务状态失败:', error);
    }
  }, [id, loadAssets]);

  const handleAnalyzeScript = async () => {
    if (!script.trim()) {
      message.warning('剧本内容为空，无法识别资产');
      return;
    }
    
    if (assets.length > 0) {
      Modal.confirm({
        title: '确认操作',
        content: '数据库里已经存在资产，是否重新执行识别资产命令？您的这个命令将会覆盖之前的数据。',
        okText: '确认执行',
        cancelText: '取消',
        onOk: async () => {
          await executeAnalyzeScript();
        }
      });
    } else {
      await executeAnalyzeScript();
    }
  };

  const handleAppendAssets = async () => {
    if (!script.trim()) {
      message.warning('剧本内容为空，无法识别资产');
      return;
    }
    
    Modal.confirm({
      title: '确认操作',
      content: '是否根据剧本追加新资产？不会覆盖现有资产，只会添加新增的资产。',
      okText: '确认执行',
      cancelText: '取消',
      onOk: async () => {
        setIsAnalyzing(true);
        setIsProgressModalVisible(true);
        
        try {
          const startResponse = await api.post('/ai/append-assets', {
            script,
            project_id: id
          });
          
          const { task_id } = startResponse.data;
          
          // 清除之前的轮询（如果有）
          if (intervalIdRef.current) {
            clearInterval(intervalIdRef.current);
            intervalIdRef.current = null;
          }
          
          // 轮询任务状态
          intervalIdRef.current = setInterval(async () => {
            await pollAnalysisTaskStatus(task_id);
          }, 1000);
          
        } catch (error) {
          setIsProgressModalVisible(false);
          setIsAnalyzing(false);
          if (intervalIdRef.current) {
            clearInterval(intervalIdRef.current);
            intervalIdRef.current = null;
          }
          message.error('启动追加资产任务失败: ' + (error.response?.data?.error || error.message));
        }
      }
    });
  };

  const executeAnalyzeScript = async () => {
    setIsAnalyzing(true);
    setIsProgressModalVisible(true);
    
    try {
      const startResponse = await api.post('/ai/analyze-script', {
        script,
        project_id: id
      });
      
      const { task_id } = startResponse.data;
      
      // 清除之前的轮询（如果有）
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
      
      // 轮询任务状态
      intervalIdRef.current = setInterval(async () => {
        await pollAnalysisTaskStatus(task_id);
      }, 1000);
      
    } catch (error) {
      setIsProgressModalVisible(false);
      setIsAnalyzing(false);
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
      message.error('启动分析失败: ' + (error.response?.data?.error || error.message));
    }
  };

  return (
    <div>
      {/* 返回按钮 */}
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(`/project/${id}`)}
        style={{ marginBottom: 16 }}
      >
        返回项目管理
      </Button>

      <Card
        title={
          <span>
            <span style={{ marginRight: 8 }}>资产管理</span>
          </span>
        }
        extra={
          <Space>
            <Button
              icon={<PlayCircleOutlined />}
              onClick={handleAnalyzeScript}
              loading={isAnalyzing}
            >
              识别资产
            </Button>
            <Button
              icon={<PlusOutlined />}
              onClick={handleAppendAssets}
              loading={isAnalyzing}
            >
              追加资产
            </Button>
            {selectedRowKeys.length > 0 && (
              <>
                <span style={{ color: '#1890ff' }}>已选择 {selectedRowKeys.length} 项</span>
                <Button
                  type="primary"
                  icon={<PictureOutlined />}
                  onClick={openBatchGenerateModal}
                >
                  批量出图
                </Button>
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={handleBatchDelete}
                >
                  批量删除
                </Button>
              </>
            )}
            {/* 导入导出资产按钮 */}
            <Button
              icon={<ImportOutlined />}
              onClick={() => setIsImportModalVisible(true)}
            >
              导入资产
            </Button>
            <Button
              icon={<ExportOutlined />}
              onClick={() => setIsExportModalVisible(true)}
              disabled={assets.length === 0}
            >
              导出资产
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openCreateModal}
            >
              添加资产
            </Button>
          </Space>
        }
      >
        <Tabs activeKey={activeTab} onChange={(key) => {
          setActiveTab(key);
          setSelectedRowKeys([]); // 切换标签时清空选择
        }}>
          <TabPane tab="角色" key="角色">
            <Table 
              dataSource={filteredAssets} 
              columns={columns} 
              rowKey="id"
              rowSelection={rowSelection}
            />
          </TabPane>
          <TabPane tab="道具" key="道具">
            <Table 
              dataSource={filteredAssets} 
              columns={columns} 
              rowKey="id"
              rowSelection={rowSelection}
            />
          </TabPane>
          <TabPane tab="场景" key="场景">
            <Table 
              dataSource={filteredAssets} 
              columns={columns} 
              rowKey="id"
              rowSelection={rowSelection}
            />
          </TabPane>
        </Tabs>
      </Card>

      {/* 资产编辑/创建模态框 */}
      <Modal 
        title={editingAsset ? '编辑资产' : '添加资产'} 
        visible={isModalVisible} 
        onCancel={handleModalCancel}
        width={700}
        footer={null}
      >
        <Form form={form} onFinish={handleCreateAsset} layout="vertical">
          <Form.Item name="type" label="类型" rules={[{ required: true }]}>
            <Select>
              <Option value="角色">角色</Option>
              <Option value="道具">道具</Option>
              <Option value="场景">场景</Option>
            </Select>
          </Form.Item>
          
          <Form.Item name="name" label="名称" rules={[{ required: true, message: '请输入名称' }]}>
            <Input placeholder="名称" />
          </Form.Item>
          
          <Form.Item name="prompt" label="生图提示词">
            <TextArea 
              rows={4} 
              placeholder="用于AI生图的提示词" 
            />
          </Form.Item>
          
          <Form.Item name="description" label="主体描述">
            <TextArea rows={4} placeholder="该资产的详细描述" />
          </Form.Item>
          
          <Form.Item label="参考图">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Upload
                accept="image/*"
                showUploadList={false}
                maxCount={5}
                customRequest={async ({ file, onSuccess, onError }) => {
                  try {
                    // 创建FormData上传文件
                    const formData = new FormData();
                    formData.append('image', file);
                    formData.append('project_id', id);
                    formData.append('asset_type', form.getFieldValue('type') || '角色');
                    formData.append('asset_name', form.getFieldValue('name') || '');
                    
                    // 调用上传API
                    const uploadUrl = (process.env.REACT_APP_API_URL || '/api') + '/upload/image';
                    const response = await fetch(uploadUrl, {
                      method: 'POST',
                      headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                      },
                      body: formData
                    });

                    const result = await response.json();
                    if (result.success) {
                      // 构建完整URL用于预览（使用缩略图）
                      const fullUrl = buildImageUrl(result.image_path, true);
                      // 添加到预览图片列表
                      setPreviewImages(prev => {
                        const newImages = [...prev, fullUrl];
                        // 保存路径到表单 - 从URL中提取相对路径
                        const imagePaths = newImages.map(imgUrl => {
                          try {
                            let path = imgUrl;
                            
                            // 如果是完整 URL，提取 pathname
                            if (imgUrl.startsWith('http')) {
                              const url = new URL(imgUrl);
                              path = url.pathname;
                            }
                            
                            // 去掉前导 /
                            if (path.startsWith('/')) {
                              path = path.substring(1);
                            }
                            // 去掉时间戳参数（如果有）
                            if (path.includes('?')) {
                              path = path.split('?')[0];
                            }
                            return path;
                          } catch (e) {
                            return imgUrl;
                          }
                        });
                        form.setFieldsValue({ image_path: imagePaths });
                        return newImages;
                      });
                      message.success('图片上传成功');
                      onSuccess();
                    } else {
                      message.error(result.error || '上传失败');
                      onError();
                    }
                  } catch (error) {
                    message.error('上传失败: ' + error.message);
                    onError();
                  }
                }}
              >
                <Button icon={<UploadOutlined />}>上传参考图片</Button>
              </Upload>
              
              {previewImages.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                  {previewImages.map((image, index) => (
                    <div key={index} style={{ 
                      width: 100, 
                      height: 100, 
                      border: '1px solid #d9d9d9', 
                      borderRadius: 4,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      position: 'relative'
                    }}>
                      <img 
                        src={image} 
                        alt={`预览 ${index + 1}`} 
                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} 
                      />
                      <button 
                        style={{
                          position: 'absolute',
                          top: 2,
                          right: 2,
                          backgroundColor: 'rgba(0,0,0,0.5)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '50%',
                          width: 20,
                          height: 20,
                          fontSize: 12,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          const newImages = previewImages.filter((_, i) => i !== index);
                          setPreviewImages(newImages);
                          // 更新表单值 - 从URL中提取相对路径
                          const imagePaths = newImages.map(imgUrl => {
                            try {
                              let path = imgUrl;
                              
                              // 如果是完整 URL，提取 pathname
                              if (imgUrl.startsWith('http')) {
                                const url = new URL(imgUrl);
                                path = url.pathname;
                              }
                              
                              // 去掉前导 /
                              if (path.startsWith('/')) {
                                path = path.substring(1);
                              }
                              // 去掉时间戳参数（如果有）
                              if (path.includes('?')) {
                                path = path.split('?')[0];
                              }
                              return path;
                            } catch (e) {
                              return imgUrl;
                            }
                          });
                          // 保持与上传时一致的格式，使用数组
                          form.setFieldsValue({ image_path: imagePaths });
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ 
                  width: 200, 
                  height: 200, 
                  border: '1px solid #d9d9d9', 
                  borderRadius: 4,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#f5f5f5',
                  color: '#999'
                }}>
                  无预览图
                </div>
              )}
              <div style={{ fontSize: 12, color: '#999' }}>
                最多上传5张图片
              </div>
            </Space>
          </Form.Item>
          
          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <Button 
                type="primary" 
                icon={<PictureOutlined />}
                onClick={handleGenerateImage}
                loading={isGenerating}
              >
                AI生图
              </Button>
              <Space>
                <Button onClick={handleModalCancel}>取消</Button>
                <Button type="primary" htmlType="submit">
                  {editingAsset ? '更新' : '创建'}
                </Button>
              </Space>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 分析进度条弹窗 */}
      <Modal
        title="正在分析剧本..."
        visible={isProgressModalVisible}
        closable={false}
        maskClosable={false}
        footer={null}
        width={500}
      >
        <div style={{ padding: '20px 0' }}>
          <Progress
            percent={analysisProgress.total > 0 ? Math.round((analysisProgress.current / analysisProgress.total) * 100) : 0}
            status="active"
            strokeColor={{ from: '#108ee9', to: '#87d068' }}
          />
          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <div style={{ fontSize: 16, fontWeight: 'bold', color: '#1890ff' }}>
              {analysisProgress.episode_title || '准备中...'}
            </div>
            <div style={{ marginTop: 8, color: '#666' }}>
              进度: {analysisProgress.current} / {analysisProgress.total} 集
            </div>
          </div>
          <div style={{ marginTop: 16, padding: 12, background: '#f5f5f5', borderRadius: 4, fontSize: 12, color: '#666' }}>
            <div>正在使用AI大模型分析剧本内容，识别角色、道具和场景...</div>
            <div style={{ marginTop: 4 }}>每集分析大约需要5-15秒，请耐心等待</div>
          </div>
        </div>
      </Modal>

      {/* 导入资产模态框 */}
      <Modal
        title="导入资产"
        visible={isImportModalVisible}
        onCancel={() => {
          setIsImportModalVisible(false);
          setImportFile(null);
        }}
        footer={null}
      >
        <div style={{ padding: '20px 0' }}>
          <div style={{ marginBottom: 16 }}>
            <p style={{ color: '#666', marginBottom: 16 }}>
              支持导入 JSON 格式或 Excel 格式的资产文件。
            </p>
            <p style={{ color: '#999', fontSize: 12 }}>
              JSON 格式示例：<br/>
              {`[{ "name": "角色名", "type": "角色", "description": "描述", "features": "特征", "prompt": "生图提示词" }]`}
            </p>
          </div>
          
          <Upload
            accept=".json,.xlsx,.xls"
            beforeUpload={(file) => {
              setImportFile(file);
              return false; // 阻止自动上传
            }}
            onRemove={() => setImportFile(null)}
            maxCount={1}
          >
            <Button icon={<UploadOutlined />}>选择文件</Button>
          </Upload>
          
          {importFile && (
            <div style={{ marginTop: 16, padding: 12, background: '#f6ffed', borderRadius: 4 }}>
              <span style={{ color: '#52c41a' }}>已选择: {importFile.name}</span>
            </div>
          )}
          
          <Space style={{ width: '100%', justifyContent: 'flex-end', marginTop: 24 }}>
            <Button onClick={() => {
              setIsImportModalVisible(false);
              setImportFile(null);
            }}>
              取消
            </Button>
            <Button 
              type="primary" 
              icon={<ImportOutlined />}
              disabled={!importFile}
              onClick={async () => {
                if (!importFile) {
                  message.warning('请先选择文件');
                  return;
                }
                
                try {
                  const fileExtension = importFile.name.split('.').pop().toLowerCase();
                  let assetsData = [];
                  
                  if (fileExtension === 'json') {
                    // 读取 JSON 文件
                    const content = await importFile.text();
                    assetsData = JSON.parse(content);
                  } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
                    // 读取 Excel 文件
                    const XLSX = await import('xlsx');
                    const data = await importFile.arrayBuffer();
                    const workbook = XLSX.read(data, { type: 'array' });
                    const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
                    assetsData = XLSX.utils.sheet_to_json(firstSheet);
                  } else {
                    message.error('不支持的文件格式，请上传 JSON 或 Excel 文件');
                    return;
                  }
                  
                  // 验证数据格式
                  if (!Array.isArray(assetsData)) {
                    message.error('文件格式错误：数据必须是数组');
                    return;
                  }
                  
                  // 导入资产
                  let successCount = 0;
                  let errorCount = 0;
                  
                  for (const assetData of assetsData) {
                    try {
                      // 映射字段
                      const assetPayload = {
                        name: assetData.name || assetData['名称'] || '',
                        type: assetData.type || assetData['类型'] || activeTab,
                        description: assetData.description || assetData['描述'] || '',
                        features: assetData.features || assetData['特征'] || '',
                        prompt: assetData.prompt || assetData['生图提示词'] || '',
                        episodes: assetData.episodes || assetData['出现集数'] || '',
                        project_id: id
                      };
                      
                      if (!assetPayload.name) {
                        console.warn('跳过无名资产:', assetData);
                        errorCount++;
                        continue;
                      }
                      
                      await assetApi.create(id, assetPayload);
                      successCount++;
                    } catch (error) {
                      console.error('导入资产失败:', error);
                      errorCount++;
                    }
                  }
                  
                  message.success(`导入完成：成功 ${successCount} 个，失败 ${errorCount} 个`);
                  setIsImportModalVisible(false);
                  setImportFile(null);
                  loadAssets();
                } catch (error) {
                  message.error('导入失败: ' + error.message);
                }
              }}
            >
              开始导入
            </Button>
          </Space>
        </div>
      </Modal>

      {/* 导出资产模态框 */}
      <Modal
        title="导出资产"
        visible={isExportModalVisible}
        onCancel={() => setIsExportModalVisible(false)}
        footer={null}
      >
        <div style={{ padding: '20px 0' }}>
          <p style={{ color: '#666', marginBottom: 24 }}>
            当前共有 <strong>{assets.length}</strong> 个资产，请选择导出格式：
          </p>
          
          <Space direction="vertical" style={{ width: '100%' }}>
            <Button 
              icon={<FileTextOutlined />} 
              size="large"
              style={{ width: '100%', textAlign: 'left' }}
              onClick={() => {
                // 导出为 JSON
                const exportData = assets.map(asset => ({
                  name: asset.name,
                  type: asset.type,
                  description: asset.description,
                  features: asset.features,
                  prompt: asset.prompt,
                  episodes: asset.episodes,
                  image_path: asset.image_path
                }));
                
                const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `assets_${id}_${new Date().toISOString().slice(0, 10)}.json`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
                
                message.success('JSON 格式导出成功');
                setIsExportModalVisible(false);
              }}
            >
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontWeight: 'bold' }}>导出为 JSON 格式</div>
                <div style={{ fontSize: 12, color: '#999' }}>适合程序处理，包含完整数据</div>
              </div>
            </Button>
            
            <Button 
              icon={<FileExcelOutlined />} 
              size="large"
              style={{ width: '100%', textAlign: 'left' }}
              onClick={async () => {
                try {
                  // 动态导入 xlsx 库
                  const XLSX = await import('xlsx');
                  
                  // 准备数据
                  const exportData = assets.map(asset => ({
                    '名称': asset.name,
                    '类型': asset.type,
                    '描述': asset.description,
                    '特征': asset.features,
                    '生图提示词': asset.prompt,
                    '出现集数': asset.episodes,
                    '图片路径': asset.image_path
                  }));
                  
                  // 创建工作簿
                  const worksheet = XLSX.utils.json_to_sheet(exportData);
                  const workbook = XLSX.utils.book_new();
                  XLSX.utils.book_append_sheet(workbook, worksheet, '资产列表');
                  
                  // 导出文件
                  XLSX.writeFile(workbook, `assets_${id}_${new Date().toISOString().slice(0, 10)}.xlsx`);
                  
                  message.success('Excel 格式导出成功');
                  setIsExportModalVisible(false);
                } catch (error) {
                  message.error('导出 Excel 失败: ' + error.message);
                }
              }}
            >
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontWeight: 'bold' }}>导出为 Excel 格式</div>
                <div style={{ fontSize: 12, color: '#999' }}>适合人工查看和编辑</div>
              </div>
            </Button>
          </Space>
          
          <div style={{ marginTop: 24, textAlign: 'right' }}>
            <Button onClick={() => setIsExportModalVisible(false)}>取消</Button>
          </div>
        </div>
      </Modal>

      {/* 批量出图设置模态框 */}
      <Modal
        title="批量出图设置"
        visible={isBatchGenerateModalVisible}
        onCancel={() => setIsBatchGenerateModalVisible(false)}
        footer={null}
      >
        <Form form={batchGenerateForm} onFinish={handleBatchGenerate} layout="vertical">
          <Form.Item 
            name="aspect_ratio" 
            label="图片比例"
            rules={[{ required: true, message: '请选择图片比例' }]}
          >
            <Select placeholder="选择图片比例">
              <Option value="1:1">1:1 (正方形)</Option>
              <Option value="16:9">16:9 (宽屏)</Option>
              <Option value="9:16">9:16 (竖屏)</Option>
              <Option value="4:3">4:3 (标准)</Option>
              <Option value="3:4">3:4 (竖版)</Option>
              <Option value="2:3">2:3 (竖版)</Option>
            </Select>
          </Form.Item>
          
          <Form.Item 
            name="orientation" 
            label="横竖方向"
            rules={[{ required: true, message: '请选择横竖方向' }]}
          >
            <Select placeholder="选择横竖方向">
              <Option value="square">正方形</Option>
              <Option value="landscape">横版</Option>
              <Option value="portrait">竖版</Option>
            </Select>
          </Form.Item>
          
          <Form.Item>
            <div style={{ marginBottom: 16, color: '#666' }}>
              将为选中的 <strong>{selectedRowKeys.length}</strong> 个资产生成图片，
              每个资产将使用其对应的<strong>生图提示词</strong>和<strong>主体描述</strong>。
            </div>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => setIsBatchGenerateModalVisible(false)}>取消</Button>
              <Button 
                type="primary" 
                icon={<PictureOutlined />}
                htmlType="submit"
                loading={isBatchGenerating}
              >
                开始批量出图
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 图片预览模态框 */}
      <Modal
        title="图片预览"
        visible={previewImageModalVisible}
        onCancel={() => {
          console.log('模态框关闭按钮被点击');
          setPreviewImageModalVisible(false);
          setImageScale(1);
          setImagePosition({ x: 0, y: 0 });
          setIsDragging(false);
        }}
        onOk={() => {
          console.log('模态框确定按钮被点击');
          setPreviewImageModalVisible(false);
          setImageScale(1);
          setImagePosition({ x: 0, y: 0 });
          setIsDragging(false);
        }}
        footer={[
          <Button key="reset" onClick={() => {
            setImageScale(1);
            setImagePosition({ x: 0, y: 0 });
          }}>
            重置
          </Button>,
          <Button key="zoom-out" onClick={() => {
            setImageScale(prev => Math.max(0.1, prev - 0.1));
          }}>
            缩小
          </Button>,
          <Button key="zoom-in" onClick={() => {
            setImageScale(prev => Math.min(5, prev + 0.1));
          }}>
            放大
          </Button>,
          <Button key="close" onClick={() => {
            console.log('关闭按钮被点击');
            setPreviewImageModalVisible(false);
            setImageScale(1);
            setImagePosition({ x: 0, y: 0 });
            setIsDragging(false);
          }}>
            关闭
          </Button>
        ]}
        width="80%"
        style={{ top: 20 }}
      >
        <div 
          style={{ 
            textAlign: 'center', 
            padding: '20px',
            position: 'relative',
            height: '70vh',
            overflow: 'auto',
            border: '1px solid #f0f0f0',
            borderRadius: 4
          }}
          onWheel={(e) => {
            e.preventDefault();
            const delta = e.deltaY > 0 ? -0.1 : 0.1;
            setImageScale(prev => Math.max(0.1, Math.min(5, prev + delta)));
          }}
        >
          <div
            style={{
              display: 'inline-block',
              position: 'relative',
              transform: `scale(${imageScale}) translate(${imagePosition.x}px, ${imagePosition.y}px)`,
              transformOrigin: 'center center',
              cursor: isDragging ? 'grabbing' : 'grab'
            }}
            onMouseDown={(e) => {
              if (imageScale > 1) {
                setIsDragging(true);
                setDragStart({
                  x: e.clientX - imagePosition.x,
                  y: e.clientY - imagePosition.y
                });
              }
            }}
            onMouseMove={(e) => {
              if (isDragging) {
                setImagePosition({
                  x: e.clientX - dragStart.x,
                  y: e.clientY - dragStart.y
                });
              }
            }}
            onMouseUp={() => {
              setIsDragging(false);
            }}
            onMouseLeave={() => {
              setIsDragging(false);
            }}
          >
            <img
              src={previewImageUrl}
              alt="预览图片"
              style={{ maxWidth: '100%' }}
              onLoad={() => {
                console.log('预览图片加载成功，URL:', previewImageUrl);
              }}
              onError={(e) => {
                console.error('预览图片加载失败，URL:', previewImageUrl, '错误:', e);
                e.target.onerror = null;
                message.error('图片加载失败');
              }}
            />
          </div>
          <div style={{ 
            position: 'absolute', 
            top: 10, 
            right: 10, 
            backgroundColor: 'rgba(255, 255, 255, 0.8)', 
            padding: '5px 10px', 
            borderRadius: 4, 
            fontSize: '12px'
          }}>
            缩放: {(imageScale * 100).toFixed(0)}%
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AssetsPage;
